// Reusable confirmation dialog for destructive actions. Two modes:
//   - simple yes/no (default)
//   - type-to-confirm: caller passes `confirmText`; the action button is
//     disabled until the user types it verbatim. Matches the GitHub/Linear
//     pattern for "delete this thing forever" affordances.
//
// Controlled like the other glass dialogs in this app — parent owns
// `open` + `onOpenChange`. Avoids the focus-trap conflict we hit when
// dialogs were nested inside Base UI Select portals.

import { Dialog } from "@base-ui-components/react/dialog";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Pill } from "@/components/glass";

interface ConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	body: React.ReactNode;
	/** Action button label (e.g. "Delete project", "Disable"). */
	confirmLabel: string;
	/**
	 * If provided, switches to type-to-confirm mode. The action button is
	 * disabled until the user types exactly this string.
	 */
	confirmText?: string;
	/** "danger" turns the action button red. */
	tone?: "danger" | "default";
	/** Async — dialog stays open until this resolves (so we show spinner). */
	onConfirm: () => Promise<void> | void;
}

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	body,
	confirmLabel,
	confirmText,
	tone = "default",
	onConfirm,
}: ConfirmDialogProps) {
	const [typed, setTyped] = useState("");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) {
			setTyped("");
			setPending(false);
			setError(null);
		}
	}, [open]);

	const ready = confirmText ? typed === confirmText : true;
	const dangerClass =
		tone === "danger"
			? "!text-[var(--color-danger)] !border-[rgba(239,68,68,0.4)]"
			: "";

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!ready || pending) return;
		setPending(true);
		setError(null);
		try {
			await onConfirm();
			onOpenChange(false);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setPending(false);
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 transition-opacity duration-150" />
				<Dialog.Popup
					className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[440px] max-w-[calc(100vw-32px)] rounded-[var(--radius-panel)] p-5 data-[starting-style]:opacity-0 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[ending-style]:scale-95 transition-all duration-150"
					style={{
						background: "rgb(20 20 23 / 0.98)",
						backdropFilter: "blur(20px) saturate(180%)",
						WebkitBackdropFilter: "blur(20px) saturate(180%)",
						border: "1px solid rgb(255 255 255 / 0.1)",
						boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
					}}
				>
					<div className="flex items-center justify-between mb-3">
						<Dialog.Title className="text-[14px] font-medium">{title}</Dialog.Title>
						<Dialog.Close className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors">
							<X className="w-4 h-4" />
						</Dialog.Close>
					</div>
					<form onSubmit={submit} className="space-y-3 text-[13px] text-[var(--color-text-muted)]">
						<div>{body}</div>
						{confirmText && (
							<div className="space-y-1">
								<label className="text-[11px] text-[var(--color-text-dim)]">
									Type{" "}
									<span className="mono text-[var(--color-text)] normal-case">
										{confirmText}
									</span>{" "}
									to confirm
								</label>
								<input
									type="text"
									value={typed}
									onChange={(e) => setTyped(e.target.value)}
									autoFocus
									autoComplete="off"
									spellCheck={false}
									className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] mono text-[13px] outline-none focus:border-[var(--color-border-hi)]"
								/>
							</div>
						)}
						{error && (
							<div className="text-[12px] text-[var(--color-danger)]">{error}</div>
						)}
						<div className="flex justify-end gap-2 pt-1">
							<Dialog.Close render={<Pill>Cancel</Pill>} />
							<Pill type="submit" disabled={!ready || pending} className={dangerClass}>
								{pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
								{confirmLabel}
							</Pill>
						</div>
					</form>
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
